import { useEffect, useState } from 'react';
import { useParams } from 'react-router';

// Deep import: the '@/common/components' barrel imports hooks, so importing it from a hook is an import cycle.
import { toPeriodKey } from '@/common/components/performance-review/period-select/period';
import { EmployeeRole, EmployeeStatus, ProbationStatus, useGetEmployeeQuery } from '@/common/graphql';
import {
  type EmployeeContractHistoryType,
  EmployeeDetail,
  type EmployeeDetailType,
  type EmployeeGeneralInformationType,
  type EmployeeJobHistoryType,
  type EmployeeManagerType,
  type EmployeeOffBoardingType,
  type EmployeePermissionEmployeeType,
  type EmployeePermissionType,
  type EmployeePersonalInformationType,
  type PerformanceReviewDetailType,
  type ProbationReviewDetailType,
  type ProbationReviewStageType,
  parseExternalAccount,
} from '@/common/types';
import type { CountryCodeType } from '@/common/utils';

export const useEmployeeInParams = () => {
  const { id = '' } = useParams();

  const {
    data: employeeData,
    loading,
    error,
  } = useGetEmployeeQuery({
    variables: { id },
    skip: !id,
    fetchPolicy: 'no-cache',
  });

  const [employee, setEmployee] = useState<EmployeeDetailType | undefined | null>(undefined);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!employeeData?.employee) {
      setEmployee(null);
      return;
    }

    const currentEmployee = employeeData?.employee;
    setEmployee(
      EmployeeDetail.parse({
        id: currentEmployee.id,
        generalInformation: {
          ...currentEmployee,
          preferredFullName: currentEmployee.generalInformation.preferredFullName,
          legalFullName: currentEmployee.generalInformation.legalFullName,
          nameInLocalLanguage: currentEmployee.generalInformation.nameInLocalLanguage ?? null,
          japanesePhoneticName: currentEmployee.generalInformation.japanesePhoneticName ?? null,
          employeeId: currentEmployee.id,
          employeeCode: currentEmployee.currentMainContract?.employeeCode ?? null,
          email: currentEmployee.generalInformation?.companyEmail?.fullAddress ?? null,
          pictureUrl: currentEmployee.pictureUrl ?? null,
          hiredDate: currentEmployee.generalInformation.hiredDate,
          contractType: currentEmployee.currentMainContract?.contractType ?? null,
          contractEndDate: currentEmployee.currentMainContract?.effectiveDateUntil ?? null,
          effectiveDateUntil: currentEmployee.currentMainContract?.effectiveDateUntil ?? null,
          officeLocation: currentEmployee.currentPosition?.location.name ?? null,
          company: currentEmployee.currentMainContract?.company.name ?? null,
          companyId: currentEmployee.currentMainContract?.company.id ?? null,
          companyCountryId: currentEmployee.currentMainContract?.company.countryId ?? null,
          businessUnit: currentEmployee.currentPosition?.businessUnit.name ?? null,
          businessUnitId: currentEmployee.currentPosition?.businessUnit?.id ?? null,
          department: currentEmployee.currentPosition?.department.name ?? null,
          departmentId: currentEmployee.currentPosition?.department?.id ?? null,
          job: currentEmployee.currentPosition?.job.name ?? null,
          jobId: currentEmployee.currentPosition?.job?.id ?? null,
          level: currentEmployee.currentPosition?.level ?? null,
          primaryManagerId: currentEmployee.generalInformation.primaryManager?.id ?? null,
          primaryManager:
            currentEmployee.generalInformation.primaryManager?.generalInformation.preferredFullName ?? null,
          dottedLineManagerId: currentEmployee.generalInformation.dottedLineManager?.id ?? null,
          dottedLineManager:
            currentEmployee.generalInformation.dottedLineManager?.generalInformation.preferredFullName ?? null,
          needsProbationReview: currentEmployee.generalInformation.needsProbationReview,
          probationReviewEndDate: currentEmployee?.generalInformation?.probationReviewEndDate,
          isNewGraduate: currentEmployee.generalInformation.isNewGraduate,
          companyEmailAccount: currentEmployee.generalInformation.companyEmail?.account ?? null,
          companyEmailDomain: currentEmployee.generalInformation.companyEmail?.domainId ?? null,
          emailAddress: currentEmployee.generalInformation.companyEmail?.fullAddress ?? null,
          nationalityId: currentEmployee.generalInformation?.nationality?.id ?? null,
          nationality: currentEmployee.generalInformation?.nationality?.demonym ?? null,
          lastWorkingDate: currentEmployee.offBoarding?.lastWorkingDate ?? null,
          terminationDate: currentEmployee.offBoarding?.employmentEndDate ?? null,
          reasonForLeaving: currentEmployee.offBoarding?.reasonForLeaving?.name ?? null,
          hiringSource: currentEmployee.generalInformation.hiringSource ?? null,
          jobTitle: currentEmployee.currentPosition?.jobTitle ?? null,
          team: currentEmployee.currentPosition?.team ?? null,
          location: currentEmployee.currentPosition?.location.name ?? null,
          greenhouseLink: currentEmployee.generalInformation.greenhouseLink ?? null,
        } satisfies EmployeeGeneralInformationType,
        personalInformation: {
          id: currentEmployee.id,
          gender: currentEmployee.personalInformation?.gender ?? null,
          dateOfBirth: currentEmployee.personalInformation?.dateOfBirth ?? null,
          age: currentEmployee.personalInformation?.age ?? null,
          personalPhoneNumber: currentEmployee.personalInformation?.personalPhoneNumber?.number ?? null,
          personalPhoneNumberCountryCode:
            (currentEmployee.personalInformation?.personalPhoneNumber?.countryId as CountryCodeType) ?? null,
          personalPhoneNumberCountryIsdCode:
            currentEmployee.personalInformation?.personalPhoneNumber?.country?.isdCode ?? null,
          personalEmail: currentEmployee.personalInformation?.personalEmail ?? null,
          currentAddress: currentEmployee.personalInformation?.currentAddress ?? null,
          maritalStatus: currentEmployee.personalInformation?.maritalStatus ?? null,
          permanentAddress: currentEmployee.personalInformation?.permanentAddress ?? null,
          residentialCountryId: (currentEmployee.personalInformation?.residentialCountryId as CountryCodeType) ?? null,
          emergencyContacts:
            currentEmployee.personalInformation?.emergencyContacts.map(item => ({
              ...item,
              phoneNumber: item.phoneNumber.number,
              phoneNumberCountryCode: item.phoneNumber.countryId,
              phoneNumberCountryIsdCode: item.phoneNumber.country?.isdCode ?? null,
              remarks: item.remarks ?? null,
            })) ?? [],
          visas:
            currentEmployee.personalInformation?.visas.map(item => ({
              ...item,
              visaType: item.visaType ?? null,
              visaNumber: item.visaNumber ?? null,
              visaIssuedPlace: item.visaIssuedPlace ?? null,
              visaIssuedDate: item.visaIssuedDate ?? null,
              visaExpiryDate: item.visaExpiryDate ?? null,
              workPermitNumber: item.workPermitNumber ?? null,
              workPermitIssuedDate: item.workPermitIssuedDate ?? null,
              workPermitExpiryDate: item.workPermitExpiryDate ?? null,
            })) ?? [],
          dependents: currentEmployee.personalInformation?.dependencies ?? [],
          // ID
          idType: currentEmployee.personalInformation?.idType ?? null,
          idNumber: currentEmployee.personalInformation?.idNumber ?? null,
          idIssuedDate: currentEmployee.personalInformation?.idIssuedDate ?? null,
          idIssuedPlace: currentEmployee.personalInformation?.idIssuedPlace ?? null,
          idExpiryDate: currentEmployee.personalInformation?.idExpiryDate ?? null,
          // Passport
          passportNumber: currentEmployee.personalInformation?.passportNumber ?? null,
          passportName: currentEmployee.personalInformation?.passportName ?? null,
          passportIssuedPlace: currentEmployee.personalInformation?.passportIssuedPlace ?? null,
          passportIssuedDate: currentEmployee.personalInformation?.passportIssuedDate ?? null,
          passportExpiryDate: currentEmployee.personalInformation?.passportExpiryDate ?? null,
          placeOfBirth: currentEmployee.personalInformation?.placeOfBirth ?? null,
          // Social Insurance
          taxCode: currentEmployee.personalInformation?.taxCode ?? null,
          socialInsuranceNumber: currentEmployee.personalInformation?.socialInsuranceNumber ?? null,
          socialInsuranceNotes: currentEmployee.personalInformation?.socialInsuranceNotes ?? null,
          pensionVoluntaryContributionRate:
            currentEmployee.personalInformation?.pensionVoluntaryContributionRate ?? null,
          taxWithholdingRate: currentEmployee.personalInformation?.taxWithholdingRate ?? null,
          nhiDependents: currentEmployee.personalInformation?.nhiDependents ?? null,
          // Bank
          bankCountryCode: currentEmployee.personalInformation?.bank?.countryId ?? null,
          bankName: currentEmployee.personalInformation?.bank?.name ?? null,
          branchName: currentEmployee.personalInformation?.bank?.branchName ?? null,
          accountNumber: currentEmployee.personalInformation?.bank?.accountNumber ?? null,
          accountHolderName: currentEmployee.personalInformation?.bank?.accountHolderName ?? null,
          // Consulting Fee
          consultingFeeBankCountryCode: currentEmployee.personalInformation?.consultingFeeBank?.countryId ?? null,
          consultingFeeBankCurrencyId: currentEmployee.personalInformation?.consultingFeeBank?.currencyId ?? null,
          consultingFeeBankName: currentEmployee.personalInformation?.consultingFeeBank?.name ?? null,
          consultingFeeBranchName: currentEmployee.personalInformation?.consultingFeeBank?.branchName ?? null,
          consultingFeeAccountNumber: currentEmployee.personalInformation?.consultingFeeBank?.accountNumber ?? null,
          consultingFeeAccountHolderName:
            currentEmployee.personalInformation?.consultingFeeBank?.accountHolderName ?? null,
          remarks: currentEmployee.personalInformation?.remarks ?? null,
        } satisfies EmployeePersonalInformationType,
        offBoarding: {
          employeeId: currentEmployee.id,
          detailReasonForLeaving: currentEmployee.offBoarding?.detailReasonForLeaving ?? null,
          employmentEndDate: currentEmployee.offBoarding?.employmentEndDate ?? null,
          lastWorkingDate: currentEmployee.offBoarding?.lastWorkingDate ?? null,
          reasonForLeavingId: currentEmployee.offBoarding?.reasonForLeaving?.id ?? null,
          deactivateGreenhouse: currentEmployee.offBoarding?.doNotDeactivate.greenhouse ?? false,
          suspendGoogleAccount: currentEmployee.offBoarding?.doNotDeactivate.google ?? false,
          suspendEloomiAccount: currentEmployee.offBoarding?.doNotDeactivate.eloomi ?? false,
          replacementValue: currentEmployee.offBoarding?.replacementValue ?? null,
          replacementValueBasis: currentEmployee.offBoarding?.replacementValueBasis ?? [],
          rehireRecommendation: currentEmployee.offBoarding?.rehireRecommendation ?? null,
          rehireConditions: currentEmployee.offBoarding?.rehireConditions ?? [],
          rehireConditionNote: currentEmployee.offBoarding?.rehireConditionNote ?? '',
          performanceReviewExclusion: currentEmployee.offBoarding?.performanceReviewExclusionStart
            ? toPeriodKey(currentEmployee.offBoarding.performanceReviewExclusionStart)
            : null,
          performanceReviewExclusionChanged: false,
        } satisfies EmployeeOffBoardingType,
        jobHistories: currentEmployee.jobHistories.map(
          item =>
            ({
              id: item.id,
              effectiveDateStart: item.effectiveDate,
              effectiveDateEnd: item.effectiveDateUntil,
              type: item.type,
              level: item.level,
              countryId: item.location.country?.name ?? '',
              locationId: item.locationId,
              location: item.location.name,
              businessUnitId: item.businessUnitId,
              businessUnit: item.businessUnit.name,
              businessUnitDefinition:
                item.businessUnit.definitions.find(it => it.languageCode === 'en')?.content ?? null,
              divisionId: item.businessUnit.divisionId,
              division: item.businessUnit.division.name,
              departmentId: item.departmentId,
              department: item.department.name,
              departmentDefinition: item.department.definitions.find(it => it.languageCode === 'en')?.content ?? null,
              jobId: item.jobId,
              job: item.job.name,
              jobDefinition: item.job.definitions.find(it => it.languageCode === 'en')?.content ?? null,
              jobTitle: item.jobTitle ?? null,
              team: item.team ?? null,
              regionalRole: item.regionalRole,
              departmentLead: item.departmentLead,
              primaryManagerId: currentEmployee.generalInformation.primaryManager?.id ?? null,
              dottedLineManagerId: currentEmployee.generalInformation.dottedLineManager?.id ?? null,
            }) satisfies EmployeeJobHistoryType,
        ),
        contractHistories: currentEmployee.contracts.map(
          item =>
            ({
              id: item.id,
              type: item.contractType,
              companyId: item.companyId,
              companyName: item.company.name,
              effectiveDateStart: item.effectiveDate,
              effectiveDateEnd: item.effectiveDateUntil,
              isOnSecondment: item.onSecondment,
              employeeCode: item.employeeCode ?? null,
              isMain: item.isMain,
              isPreAcquisition: item.isPreAcquisition,
              companyAcquiredDate: item.company.acquiredDate,
            }) satisfies EmployeeContractHistoryType,
        ),
        jobHistoryNotes: currentEmployee.jobHistoryNote,
        contractNotes: currentEmployee.contractNote,
        externalAccounts: currentEmployee.externalAccounts?.map(parseExternalAccount),
        permission: {
          accessSpecificEmployees: currentEmployee.permission?.accessSpecificEmployees ?? false,
          companyIds: currentEmployee.permission?.companyIds ?? [],
          employeeIds: currentEmployee.permission?.employeeIds ?? [],
          role: currentEmployee.permission?.role ?? EmployeeRole.Basic,
          idpFullAccess: currentEmployee.permission?.idpFullAccess ?? false,
          employees:
            currentEmployee.permission?.employees.map(
              item =>
                ({
                  id: item.id,
                  company: item.currentMainContract?.company.name ?? '',
                  legalName: item.generalInformation.legalFullName,
                  preferredFullName: item.generalInformation.preferredFullName,
                  businessUnit: item.currentPosition?.businessUnit.name ?? '',
                  department: item.currentPosition?.department.name ?? '',
                }) satisfies EmployeePermissionEmployeeType,
            ) ?? [],
          companies: currentEmployee.permission?.companies ?? [],
        } satisfies EmployeePermissionType,
        probationReview: {
          probationStatus: currentEmployee.probationReview?.probationStatus ?? ProbationStatus.InProgress,
          outcome: currentEmployee.probationReview?.outcome ?? null,
          flags: currentEmployee.probationReview?.flags ?? [],
          expectation: currentEmployee.probationReview?.expectation ?? '',
          employeeId: currentEmployee.id,
          probationEndDate: currentEmployee.probationReview?.probationEndDate,
          nextDueDate: currentEmployee.probationReview?.nextDueDate ?? null,
          daysOverdue: currentEmployee.probationReview?.daysOverdue ?? 0,
          reviewerAbsent: currentEmployee.probationReview?.reviewerAbsent ?? false,
          extension: currentEmployee.probationReview?.extension
            ? {
                count: currentEmployee.probationReview.extension.count,
                originalEndDate: currentEmployee.probationReview.extension.originalEndDate,
                newEndDate: currentEmployee.probationReview.extension.newEndDate,
                reason: currentEmployee.probationReview.extension.reason ?? null,
              }
            : null,
          probationReviewTerm: currentEmployee.currentMainContract?.company.probationReviewTerm ?? null,
          stages:
            currentEmployee.probationReview?.reviews.map(
              item =>
                ({
                  reviewerId: item.reviewerId ?? '',
                  deadline: item.deadline ?? '',
                  reviewerFeedback: item.reviewerFeedback ?? '',
                  selfFeedback: item.selfFeedback ?? '',
                  reviewerName: item.reviewer?.generalInformation.preferredFullName ?? '',
                  reviewerHasLeft: item.reviewer?.statusBasedOnLastWorkingDate === EmployeeStatus.Inactive,
                  assessments: item.assessments.map(assessment => ({
                    ...assessment,
                    assessment: {
                      self: assessment.assessment.self ?? null,
                      reviewer: assessment.assessment.reviewer ?? null,
                    },
                  })),
                  reviewStatus: item.reviewStatus,
                  completionMethod: item.completionMethod ?? null,
                  submittedAt: item.submittedAt ?? null,
                  isExtension: item.isExtension,
                  extensionReason: item.extensionReason ?? null,
                  needAssessments: item.needAssessments ?? false,
                }) satisfies ProbationReviewStageType,
            ) ?? [],
        } satisfies ProbationReviewDetailType,
        managers: currentEmployee.managers.map(
          item =>
            ({
              id: item.id,
              name: item.generalInformation.preferredFullName,
              hasLeft: item.statusBasedOnLastWorkingDate === EmployeeStatus.Inactive,
            }) satisfies EmployeeManagerType,
        ),
        performanceReviews: currentEmployee.performanceReviews.map(
          item =>
            ({
              id: item.id,
              stage: item.setting.stage,
              year: item.period.year,
              quarter: item.period.quarter,
              version: item.period.version,
              isCurrent: item.period.isCurrent,
              effectivePeriod: {
                start: item.period.effectivePeriod.start,
                end: item.period.effectivePeriod.end,
              },
            }) satisfies PerformanceReviewDetailType,
        ),
        googleProductAndSKUForNew: currentEmployee?.googleProductAndSKUForNew,
      }),
    );
  }, [employeeData?.employee, loading]);

  return {
    id,
    employee,
    employeeLoading: loading,
    employeeError: error,
  };
};
